"use client";
import React, { useCallback, useState } from "react";
import { BsCaretDownFill, BsCaretUpFill } from "react-icons/bs";

import { EmptyTable } from "components/EmptyTable";
import FullSizeLoading from "components/FullSizeLoading";
import ParkingTableRow from "components/ParkingTableRow";
import { SORT_DESTINATION, SortDestination } from "constants/sort";
import { LessorPropertyList, LessorPropertyListOrder } from "graphql/type";
import { cn } from "utils/cn";

type ParkingLot = {
  imageUrl: string;
  name: string;
  address: string;
};

export type ParkingLotRowData = {
  parkingLot: ParkingLot;
  propertyNumber: string;
  status: boolean;
  numberOfFreeSchedule: number; // 空き予定台数
  numberOfFree: number; // 空車台数
  full: number;
  remark: string;
  personInCharge: string;
  publicSetting: boolean;
};

export const UnSortableField = ({ name }: { name: string }) => (
  <div className="flex items-center justify-start border-r pl-2 text-[12px] text-gray">{name}</div>
);

const { ASC, DESC } = SORT_DESTINATION;

type SortTableFieldProps<T> = {
  field?: keyof T;
  onSort: (field: keyof T) => void;
  name: string;
  sortDirection: SortDestination | "";
  sortField: keyof T | null;
  index?: number;
};

export const SortTableField = <T,>({
  field,
  onSort,
  name,
  sortDirection,
  sortField,
  index,
}: SortTableFieldProps<T>) => {
  const handleOnSort = useCallback(() => {
    if (field == null) return;
    onSort(field);
  }, [field, onSort]);

  const isActive = field === sortField;
  const isAscending = sortDirection === ASC;
  const isDescending = sortDirection === DESC;
  const showSortIcon = field != null;

  return (
    <div
      className={cn(
        "flex items-center justify-start whitespace-nowrap border-r text-[12px] text-gray",
        field && "cursor-pointer",
        index == 0 ? "" : "pl-[14px]"
      )}
      onClick={handleOnSort}
    >
      <span className={cn("text-gray", sortDirection && isActive && "text-black")}>{name}</span>
      {showSortIcon &&
        (isAscending && isActive ? (
          <BsCaretUpFill className="ml-2 text-gray" />
        ) : (
          <BsCaretDownFill className={cn("ml-2 text-disabled", isDescending && isActive && "text-gray")} />
        ))}
    </div>
  );
};

export const SortTableFieldForAnnouncement = <T,>({
  field,
  onSort,
  name,
  sortDirection,
  sortField,
  index,
}: SortTableFieldProps<T>) => {
  const handleOnSort = useCallback(() => {
    if (field == null) return;
    onSort(field);
  }, [field, onSort]);

  const isActive = field === sortField;
  const isAscending = sortDirection === ASC;
  const isDescending = sortDirection === DESC;
  const showSortIcon = field != null;

  return (
    <div
      className={cn(
        "flex items-center justify-start whitespace-nowrap border-r text-[12px] text-gray",
        field && "cursor-pointer",
        index == 0 ? "" : "pl-[14px]"
      )}
      onClick={handleOnSort}
    >
      <span className={cn("text-gray", sortDirection && isActive && "text-black")}>{name}</span>
      {showSortIcon &&
        (isAscending && isActive ? (
          <BsCaretUpFill className="ml-2 text-black" />
        ) : (
          <BsCaretDownFill className={cn("ml-2 text-disabled", isDescending && isActive && "text-black")} />
        ))}
    </div>
  );
};

type ParkingTableProps = {
  data?: LessorPropertyList[];
  isValidating?: boolean;
  onSetSort: (field: keyof LessorPropertyListOrder, direction: SortDestination) => void;
  updateMemo: (id: string, memo: string) => void;
};

const ParkingTable = ({ data, onSetSort, isValidating, updateMemo }: ParkingTableProps) => {
  const [sortField, setSortField] = useState<keyof LessorPropertyListOrder | null>(null); // 並び替えのフィールド
  const [sortDirection, setSortDirection] = useState<SortDestination | "">(""); // 並び替えの方向

  const onSort = (field: keyof LessorPropertyListOrder) => {
    const newSortDirection = sortField !== field ? DESC : sortDirection === ASC ? DESC : ASC;
    setSortField(field);
    setSortDirection(newSortDirection);
    onSetSort(field, newSortDirection);
  };

  const headers: { name: string; field?: keyof LessorPropertyListOrder }[] = [
    { name: "駐車場", field: "name" },
    { name: "物件No.", field: "controlNumber" },
    { name: "備考" },
    { name: "空車", field: "propertyEmptySpaceNumber" },
    { name: "満車", field: "propertyOccupiedSpaceNumber" },
    { name: "担当", field: "officerName" },
    { name: "公開設定", field: "visibilityPriority" },
  ];

  return (
    <div className="flex flex-1 flex-col">
      <div className="sticky top-0 mb-2 mt-4 grid h-8 grid-cols-table rounded bg-border px-4 ">
        {headers.map(({ name, field }, index) => (
          <SortTableField<LessorPropertyListOrder>
            key={index}
            field={field}
            onSort={onSort}
            name={name}
            sortDirection={sortDirection}
            sortField={sortField}
            index={index}
          />
        ))}
      </div>
      {isValidating ? (
        <FullSizeLoading />
      ) : data && data.length > 0 ? (
        data.map((row, rowIndex) => <ParkingTableRow key={rowIndex} parkingLotData={row} updateMemo={updateMemo} />)
      ) : (
        <EmptyTable buttonLabel="物件を新規登録" href="/property/create" label="物件を登録してください" />
      )}
    </div>
  );
};

export default ParkingTable;
